import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useCognitoAuthContext } from '@caplend/cognito-auth';
import * as API from '../../store/sagas/api';
import * as userActions from '../../store/actions/user';
import ErrorMessage from '../../components/ErrorMessage';

const resolveTosId = (searchParams) => searchParams.get('tos_id');

const isBlindPayPopupWindow = () => typeof window !== 'undefined' && (window.opener != null || window.name === 'blindpay-tos');

function BlindPayAccept() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { hasAccessToken, isCheckingSession } = useCognitoAuthContext();
  const tosId = resolveTosId(searchParams);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (isCheckingSession || !hasAccessToken) {
      return () => {
        cancelled = true;
      };
    }

    const submit = async () => {
      try {
        if (!tosId) {
          throw new Error('Missing terms of service ID. Please try again from the Terms & Conditions page.');
        }

        await API.acceptBlindPayTos(tosId);
        if (cancelled) return;
        try {
          sessionStorage.setItem('blindpay-tos-accepted', '1');
        } catch (_) {
          //
        }
        dispatch(userActions.getUser());
        dispatch(userActions.getOnboardingStatus());
        setStatus('success');
      } catch (err) {
        if (cancelled) return;
        setStatus('error');
        setError(err);
      }
    };

    submit();
    return () => {
      cancelled = true;
    };
  }, [tosId, dispatch, hasAccessToken, isCheckingSession]);

  if (isCheckingSession) {
    return (
      <div className='min-h-[75vh] flex items-center justify-center bg-background p-4'>
        <div className='w-full max-w-md rounded-lg border bg-card text-card-foreground shadow-sm p-6 text-center'>
          <p className='text-sm text-muted-foreground'>Loading session...</p>
        </div>
      </div>
    );
  }

  if (!hasAccessToken) {
    return <Navigate to='/login' state={{ from: location }} replace />;
  }

  if (status === 'loading') {
    return (
      <div className='min-h-[75vh] flex items-center justify-center bg-background p-4'>
        <div className='w-full max-w-md rounded-lg border bg-card text-card-foreground shadow-sm p-6 text-center'>
          <div className='mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10'>
            <svg className='h-6 w-6 animate-spin text-primary' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' aria-hidden='true'>
              <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
              <path
                className='opacity-75'
                fill='currentColor'
                d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
              />
            </svg>
          </div>
          <p className='text-sm text-muted-foreground'>Recording your BlindPay terms acceptance...</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    if (isBlindPayPopupWindow()) {
      try {
        window.close();
      } catch (_) {
        //
      }
      return null;
    }

    return <Navigate to='/accept-legal-agreements' replace />;
  }

  return (
    <div className='min-h-[75vh] flex items-center justify-center bg-background p-4'>
      <div className='w-full max-w-md rounded-lg border bg-card text-card-foreground shadow-sm p-6'>
        <div className='mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10'>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='24'
            height='24'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
            className='h-6 w-6 text-destructive'
          >
            <circle cx='12' cy='12' r='10' />
            <path d='M15 9l-6 6M9 9l6 6' />
          </svg>
        </div>
        <h3 className='font-semibold tracking-tight text-lg text-center text-foreground'>Something went wrong</h3>
        <ErrorMessage error={error} className='mt-2' />
        <button
          type='button'
          onClick={() => navigate('/accept-legal-agreements', { replace: true })}
          className='mt-4 w-full inline-flex items-center justify-center rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2'
        >
          Back to Terms & Conditions
        </button>
      </div>
    </div>
  );
}

export default BlindPayAccept;
